import type {
  ValueCheckLabels,
  ValueCheckResult,
} from "@/features/value-check/model";
import { VALUE_CHECK_STASH_DAYS } from "@/features/value-check/value-check.static";

/**
 * The last check, kept in this browser so "Create ad" can pick it up after login or sign-up
 * (Thomas, 27 Oct: in the browser, nothing on our servers). IndexedDB, because a photo is too big
 * for localStorage. Every read and write fails soft: private windows and blocked storage simply
 * have no stash, and create-ad then opens empty.
 */
export type ValueCheckStash = {
  photo: Blob;
  title: string | null;
  labels: ValueCheckLabels;
  savedAt: number;
};

const DB_NAME = "whoppah-value-check";
const DB_VERSION = 1;
const STORE = "checks";
/** One check at a time: a new check replaces the last. */
const KEY = "latest";
/**
 * The last answer, kept apart from the hand-off so a reload or "back" from create-ad shows it
 * again (WHO-6534 F3) after create-ad has taken and cleared the hand-off.
 */
const SHOWN_KEY = "shown";
const DAY_MS = 24 * 60 * 60 * 1000;

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const run = async <T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> => {
  const db = await openDb();

  try {
    return await new Promise<T>((resolve, reject) => {
      const request = action(db.transaction(STORE, mode).objectStore(STORE));

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
};

export const isValueCheckStashExpired = (
  stash: Pick<ValueCheckStash, "savedAt">,
  now: number = Date.now()
) => now - stash.savedAt > VALUE_CHECK_STASH_DAYS * DAY_MS;

export const saveValueCheckStash = async (stash: ValueCheckStash) => {
  try {
    await run("readwrite", store => store.put(stash, KEY));
  } catch (error) {
    console.warn("value-check: could not keep the check", error);
  }
};

export const clearValueCheckStash = async () => {
  try {
    await run("readwrite", store => store.delete(KEY));
  } catch (error) {
    console.warn("value-check: could not clear the check", error);
  }
};

/** The kept check, or null when there is none, it expired (and is dropped) or storage fails. */
export const readValueCheckStash =
  async (): Promise<ValueCheckStash | null> => {
    try {
      const stash = await run<ValueCheckStash | undefined>("readonly", store =>
        store.get(KEY)
      );

      if (!stash) {
        return null;
      }

      if (isValueCheckStashExpired(stash)) {
        await clearValueCheckStash();

        return null;
      }

      return stash;
    } catch (error) {
      console.warn("value-check: could not read the check", error);

      return null;
    }
  };

/** The last answer as the page showed it, with its photo. */
export type ValueCheckShown = {
  photo: Blob;
  result: ValueCheckResult;
  savedAt: number;
};

export const saveValueCheckShown = async (shown: ValueCheckShown) => {
  try {
    await run("readwrite", store => store.put(shown, SHOWN_KEY));
  } catch (error) {
    console.warn("value-check: could not keep the answer", error);
  }
};

export const clearValueCheckShown = async () => {
  try {
    await run("readwrite", store => store.delete(SHOWN_KEY));
  } catch (error) {
    console.warn("value-check: could not clear the answer", error);
  }
};

/** The last answer, or null when there is none, it expired (and is dropped) or storage fails. */
export const readValueCheckShown =
  async (): Promise<ValueCheckShown | null> => {
    try {
      const shown = await run<ValueCheckShown | undefined>("readonly", store =>
        store.get(SHOWN_KEY)
      );

      if (!shown?.result) {
        return null;
      }

      if (isValueCheckStashExpired(shown)) {
        await clearValueCheckShown();

        return null;
      }

      return shown;
    } catch (error) {
      console.warn("value-check: could not read the answer", error);

      return null;
    }
  };
